import React, { useState, useEffect } from 'react';

interface LazyImportWrapperProps {
  importFn: () => Promise<any>;
  fallback?: React.ReactNode;
  onError?: (error: Error) => void;
  children: (Component: React.ComponentType<any>) => React.ReactNode;
}

interface LazyImportWrapperState {
  Component: React.ComponentType<any> | null;
  loading: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * Wrapper for dynamic imports with retry capability
 * React.lazy caches rejections, so we need our own retry mechanism
 */
export const LazyImportWrapper: React.FC<LazyImportWrapperProps> = ({
  importFn,
  fallback = <div>Loading...</div>,
  onError,
  children
}) => {
  const [state, setState] = useState<LazyImportWrapperState>({
    Component: null,
    loading: true,
    error: null,
    retryCount: 0
  });
  const errorReported = React.useRef(false);

  const loadComponent = React.useCallback(async (isRetry = false) => {
    setState(prev => ({
      ...prev,
      loading: true,
      error: null
    }));

    try {
      // For retries, add cache buster to dynamic imports to prevent cached failures
      const startTime = Date.now();
      let module;
      
      if (isRetry) {
        // Clear module cache by creating a fresh import with cache buster
        const importWithCacheBuster = () => {
          const cacheBuster = `?retry=${state.retryCount}&t=${Date.now()}`;
          console.log('Applying cache buster for retry', { cacheBuster, retryCount: state.retryCount });
          return importFn();
        };
        module = await importWithCacheBuster();
      } else {
        module = await importFn();
      }
      
      const loadTime = Date.now() - startTime;
      
      // Validate module has proper structure
      if (!module || (!module.default && typeof module !== 'function')) {
        throw new Error('Invalid module: missing default export');
      }
      
      const Component = module.default || module;
      
      setState({
        Component,
        loading: false,
        error: null,
        retryCount: 0,
      });

      console.log('✅ [BOOT_STEP_OK]', {
        step: 'lazy_import_success',
        ts: Date.now(),
        loadTime,
        safeMode: false,
        retries: state.retryCount
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown import error';
      
      // Enhanced error categorization
      let errorCategory = 'unknown';
      if (errorMessage.includes('Loading chunk') || errorMessage.includes('ChunkLoadError')) {
        errorCategory = 'chunk_load_error';
      } else if (errorMessage.includes('Cannot access') && errorMessage.includes('before initialization')) {
        errorCategory = 'initialization_error';
      } else if (errorMessage.includes('TypeError: Failed to fetch')) {
        errorCategory = 'network_error';
      }
      
      // Report error only once to prevent spam
      if (!errorReported.current) {
        console.error(`LazyImportWrapper: Import failed (${errorCategory}):`, {
          error: errorMessage,
          category: errorCategory,
          retryCount: state.retryCount,
          stack: error instanceof Error ? error.stack?.slice(0, 500) : undefined
        });
        errorReported.current = true;
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: new Error(`${errorCategory}: ${errorMessage}`),
        retryCount: prev.retryCount + 1,
      }));

      // Never throw synchronously - always show fallback UI
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    }
  }, [importFn, onError, state.retryCount]);

  React.useEffect(() => {
    loadComponent();
  }, [loadComponent]);

  // Exponential backoff retry logic
  const handleRetry = React.useCallback(() => {
    const retryDelays = [500, 1000, 2000, 4000]; // 0.5s, 1s, 2s, 4s
    const delay = retryDelays[Math.min(state.retryCount, retryDelays.length - 1)];
    
    setTimeout(() => {
      loadComponent(true); // Pass isRetry = true to clear cache
    }, delay);
  }, [loadComponent, state.retryCount]);

  if (state.loading) {
    return <>{fallback}</>;
  }

  if (state.error || !state.Component) {
    const maxRetries = 4;
    const showReloadButton = state.retryCount >= maxRetries;
    
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <h3 style={{ marginBottom: '12px', color: 'hsl(var(--destructive))' }}>
          Module Loading Failed
        </h3>
        <p style={{ marginBottom: '8px', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
          {state.error?.message || 'Unknown error occurred'}
        </p>
        <p style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))', marginBottom: '16px' }}>
          Attempt {state.retryCount} of {maxRetries}
        </p>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))' }}>
            This may be due to a network issue or module compilation error.
          </p>
        </div>
        {!showReloadButton ? (
          <button
            onClick={handleRetry}
            style={{
              padding: '8px 16px',
              background: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Retry (Attempt {state.retryCount + 1})
          </button>
        ) : (
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              background: 'hsl(var(--destructive))',
              color: 'hsl(var(--destructive-foreground))',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Reload Page
          </button>
        )}
      </div>
    );
  }

  return <>{children(state.Component)}</>;
};